import { memo } from 'react';
import type { GameVariant } from '@draughts/shared';
import { getAvatarPreset } from '../profile/avatar-presets';
import { usePreferencesStore } from '../../store/preferences-store';
import { useStandalonePieceColorPair } from './use-appearance';
import PieceSwatch from '../shared/PieceSwatch';
import './UserAvatar.css';

export interface UserAvatarProps {
  username: string;
  variant: 'self' | 'opponent';
  pieceColor?: 'white' | 'black';
  size?: 'sm' | 'md';
  /** Avatar preset key; when set and valid, overrides the username initial. */
  avatar?: string | null;
  /**
   * La variante su cui risolvere il colore del segnalino col tema 'auto'.
   *
   * Si riceve e non si legge da sé: l'avatar compare anche in classifica, nella lista
   * giocatori, nei tavoli e nelle righe dello storico, dove lo store della partita in corso
   * è fermo all'ultima partita giocata. La passa chi la conosce; senza, si ricade sulla
   * variante preferita.
   */
  pieceVariant?: GameVariant;
}

const initialOf = (name: string): string => {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';
  return trimmed.charAt(0).toUpperCase();
};

export const UserAvatar = memo<UserAvatarProps>(({ username, variant, pieceColor, size = 'md', avatar, pieceVariant }) => {
  const preset = getAvatarPreset(avatar);

  // L'indicatore è un vero <Piece> in miniatura, così forma, stile 3D e colori
  // seguono le preferenze senza duplicare qui i gradienti.
  // Col tema 'auto' i colori sono quelli della variante ricevuta — in partita è la stessa
  // che disegna la scacchiera — e senza variante quelli della variante preferita.
  const indicatorColors = useStandalonePieceColorPair(pieceVariant);

  const className = [
    'user-avatar',
    `user-avatar--${variant}`,
    `user-avatar--${size}`,
    preset ? 'user-avatar--preset' : '',
  ].filter(Boolean).join(' ');

  const iconSize = size === 'sm' ? 18 : 22;

  return (
    <div
      className={className}
      data-testid="user-avatar"
      style={preset ? { background: preset.gradient } : undefined}
    >
      {preset ? preset.icon({ size: iconSize }) : initialOf(username)}
      {pieceColor && (
        <span
          className={`user-avatar-piece-indicator piece-indicator--${pieceColor}`}
          data-testid="user-avatar-piece-indicator"
          aria-hidden="true"
        >
          <PieceSwatch color={pieceColor} size={16} colorPair={indicatorColors} />
        </span>
      )}
    </div>
  );
});

UserAvatar.displayName = 'UserAvatar';
